/**
 * Guide videos: which Academy lesson has which YouTube video.
 *
 * Source of truth is the website (`blueticks.co/academy/videos.json`, generated
 * from `academy-data.ts`). We fetch it once per session so a new upload, or a
 * new pt / ar version, lights up in the app and the extension without a
 * release. A BUNDLED copy of the same data is used until (and unless) the fetch
 * succeeds, so the buttons never wait on the network and work offline.
 *
 * Contract (version 1):
 *   { version: 1, lessons: { "<slug>": { number, title, durationSec, url,
 *     videos: { en: { id }, pt?: { id }, ar?: { id } } } } }
 * Only lessons with at least one video id are listed. A lesson without one is
 * simply absent, and every button for it renders nothing.
 *
 * Extension: WhatsApp Web's page CSP blocks `connect-src` to blueticks.co, so
 * inside the extension the JSON is fetched by the background worker
 * (`fetchUrlContent`; blueticks.co is in the manifest host_permissions).
 */
import { isExtensionContext } from '@common/services/detect-context';
import { ContentMessageServiceActions } from '@extension/context/content/services/contentMessageServiceActions';
import type { ContentLang } from './guide-video-language';

export interface GuideLessonVideo {
  id: string;
}

export interface GuideLesson {
  /** "2.1" */
  number?: string | number;
  title: string;
  durationSec: number;
  /** The lesson page on blueticks.co. */
  url: string;
  videos: Partial<Record<ContentLang, GuideLessonVideo>>;
}

export interface GuideVideosRegistry {
  version: 1;
  lessons: Record<string, GuideLesson>;
}

export const GUIDE_VIDEOS_URL = 'https://blueticks.co/academy/videos.json';
export const ACADEMY_URL = 'https://blueticks.co/guides';
export const lessonPageUrl = (slug: string) => `${ACADEMY_URL}/academy/${slug}`;

const lesson = (number: string, slug: string, title: string, durationSec: number, en: string): [string, GuideLesson] => [
  slug,
  { number, title, durationSec, url: lessonPageUrl(slug), videos: { en: { id: en } } },
];

/** The bundled copy — keep in step with blueticks-website academy-data.ts. */
export const BUNDLED_GUIDE_VIDEOS: GuideVideosRegistry = {
  version: 1,
  lessons: Object.fromEntries([
    lesson('1.1', 'get-started', 'From sign-up to your first message', 54, 'YD-qdeSba-k'),
    lesson('1.2', 'connect-whatsapp', 'Connect WhatsApp: on this computer or always on', 75, 'xZXdipYA96c'),
    lesson('2.1', 'message-planner', 'Message Planner: schedule, drag, repeat', 76, 'eG9PHxK4DUE'),
    lesson('5.1', 'api', 'The Blueticks API: your first message in one request', 78, '0dj7H5i-Iy4'),
    lesson('5.2', 'webhooks', 'Webhooks: react when WhatsApp happens', 63, 'wQIwGkBt6Ok'),
    lesson('5.3', 'claude-connector', 'Use Blueticks from Claude', 71, 'qoWQXljOd7k'),
    lesson('6.1', 'free-or-pro', 'Free or Pro: what you actually get', 56, 'ziqJedxNz0s'),
  ]),
};

const YT_ID = /^[A-Za-z0-9_-]{6,20}$/;

/** Validate a fetched registry. Bad lessons are dropped; a bad document returns null. */
export function parseGuideVideos(raw: unknown): GuideVideosRegistry | null {
  if (!raw || typeof raw !== 'object') return null;
  const doc = raw as { version?: unknown; lessons?: unknown };
  if (doc.version !== 1 || !doc.lessons || typeof doc.lessons !== 'object') return null;
  const lessons: Record<string, GuideLesson> = {};
  for (const [slug, value] of Object.entries(doc.lessons as Record<string, unknown>)) {
    if (!value || typeof value !== 'object') continue;
    const l = value as Partial<GuideLesson> & { videos?: Record<string, unknown> };
    const videos: GuideLesson['videos'] = {};
    for (const [lang, v] of Object.entries(l.videos ?? {})) {
      const id = (v as { id?: unknown } | null)?.id;
      if (typeof id === 'string' && YT_ID.test(id)) videos[lang as ContentLang] = { id };
    }
    if (!Object.keys(videos).length) continue;
    lessons[slug] = {
      number: l.number,
      title: typeof l.title === 'string' ? l.title : slug,
      durationSec: typeof l.durationSec === 'number' && l.durationSec > 0 ? l.durationSec : 0,
      url: typeof l.url === 'string' && /^https:\/\/blueticks\.co\//.test(l.url) ? l.url : lessonPageUrl(slug),
      videos,
    };
  }
  return Object.keys(lessons).length ? { version: 1, lessons } : null;
}

/* ───────────── store: bundled → cached → fetched ───────────── */

const CACHE_KEY = 'bt_guide_videos_v1';
const CACHE_TTL_MS = 12 * 60 * 60 * 1000;
const FETCH_TIMEOUT_MS = 8000;

let current: GuideVideosRegistry = readCache() ?? BUNDLED_GUIDE_VIDEOS;
let inflight: Promise<GuideVideosRegistry> | null = null;
const listeners = new Set<() => void>();

function readCache(): GuideVideosRegistry | null {
  try {
    const raw = globalThis.localStorage?.getItem(CACHE_KEY);
    if (!raw) return null;
    const { at, data } = JSON.parse(raw) as { at: number; data: unknown };
    if (!at || Date.now() - at > CACHE_TTL_MS) return null;
    return parseGuideVideos(data);
  } catch {
    return null;
  }
}

function writeCache(data: GuideVideosRegistry) {
  try {
    globalThis.localStorage?.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), data }));
  } catch {
    /* storage blocked: memory still holds it */
  }
}

export type FetchText = (url: string) => Promise<string>;

const withTimeout = <T,>(p: Promise<T>, ms: number): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new Error('timeout')), ms); });
  return Promise.race([p, timeout]).finally(() => clearTimeout(timer));
};

const defaultFetchText: FetchText = async (url) => {
  if (isExtensionContext()) return ContentMessageServiceActions.fetchUrlContent(url);
  const res = await fetch(url, { credentials: 'omit' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.text();
};

export function getGuideVideos(): GuideVideosRegistry {
  return current;
}

/** Fetch the live list once per session. Any failure keeps the bundled / cached copy. */
export function refreshGuideVideos(fetchText: FetchText = defaultFetchText): Promise<GuideVideosRegistry> {
  if (inflight) return inflight;
  inflight = withTimeout(fetchText(GUIDE_VIDEOS_URL), FETCH_TIMEOUT_MS)
    .then((text) => {
      const parsed = parseGuideVideos(JSON.parse(text));
      if (parsed) {
        current = parsed;
        writeCache(parsed);
        listeners.forEach((l) => l());
      }
      return current;
    })
    .catch(() => current);
  return inflight;
}

export function subscribeGuideVideos(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The lesson, only when it has at least one video id (otherwise null: render nothing). */
export function getGuideLesson(slug: string, registry: GuideVideosRegistry = current): GuideLesson | null {
  const l = registry.lessons[slug];
  return l && Object.values(l.videos).some((v) => v?.id) ? l : null;
}

/** 75 → "1:15" */
export function formatDuration(sec: number): string {
  if (!sec || sec <= 0) return '';
  const s = Math.round(sec);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Test-only: reset the module state. */
export function __resetGuideVideosForTests(next: GuideVideosRegistry = BUNDLED_GUIDE_VIDEOS) {
  current = next;
  inflight = null;
  listeners.clear();
}
